import { Component, Input, OnInit, inject, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  CampaignsOutboundEventConfigurationApiClient,
  OutboundEventIntegrationConfigDto,
  UpdateOutboundEventConfigRequestDto,
  OutboundEvents,
  OutboundEventProvider
} from 'src/app/api/api-campaigns.service.generated';

@Component({
  selector: 'app-campaign-outbound-events',
  standalone: true,
  templateUrl: './campaign-outbound-events.component.html',
  styleUrls: ['./campaign-outbound-events.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatSnackBarModule
  ]
})
export class CampaignOutboundEventsComponent implements OnInit, OnChanges {
  @Input() campaignId: string;
  @ViewChild('eventTypeInput') eventTypeInput: ElementRef<HTMLInputElement>;

  configForm: FormGroup;
  eventTypeControl = new FormControl('');
  isLoading = false;
  isSaving = false;
  availableEventTypes: OutboundEvents[] = [];
  filteredEventTypes: Observable<OutboundEvents[]>;
  selectedEventTypes: OutboundEvents[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  providers: OutboundEventProvider[] = Object.values(OutboundEventProvider);

  allConfigs: OutboundEventIntegrationConfigDto[] = [];
  currentConfig: OutboundEventIntegrationConfigDto | null = null;
  eventTypesTouched = false;

  isCheckingConnection = false;

  private fb = inject(FormBuilder);
  private apiClient = inject(CampaignsOutboundEventConfigurationApiClient);
  private snackBar = inject(MatSnackBar);
  private http = inject(HttpClient);

  ngOnInit() {
    this.initializeForm();
    this.loadAvailableEventTypes();
    this.setupEventTypeFilter();
    if (this.campaignId) {
      this.loadConfiguration();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['campaignId'] && !changes['campaignId'].firstChange && this.campaignId) {
      this.loadConfiguration();
    }
  }

  private initializeForm() {
    this.configForm = this.fb.group({
      provider: [OutboundEventProvider.None, Validators.required],
      writeKey: [''],
      webhookEndpoint: [''],
      webhookSecurityKey: ['']
    });

    // Listen to provider changes to enable/disable fields and event types
    this.configForm.get('provider')?.valueChanges.subscribe(provider => {
      const writeKeyControl = this.configForm.get('writeKey');
      const webhookEndpointControl = this.configForm.get('webhookEndpoint');
      const webhookSecurityKeyControl = this.configForm.get('webhookSecurityKey');

      // Clear all provider-specific validators and values first
      writeKeyControl?.clearValidators();
      writeKeyControl?.disable();
      writeKeyControl?.setValue('');
      webhookEndpointControl?.clearValidators();
      webhookEndpointControl?.disable();
      webhookEndpointControl?.setValue('');
      webhookSecurityKeyControl?.clearValidators();
      webhookSecurityKeyControl?.disable();
      webhookSecurityKeyControl?.setValue('');

      if (provider === OutboundEventProvider.None) {
        this.eventTypeControl.disable();
        this.selectedEventTypes = [];
        this.eventTypesTouched = false;
      } else if (provider === OutboundEventProvider.Segment) {
        writeKeyControl?.setValidators([Validators.required]);
        writeKeyControl?.enable();
        writeKeyControl?.markAsTouched();
        this.eventTypeControl.enable();
        this.eventTypesTouched = true;
      } else if (provider === OutboundEventProvider.Webhook) {
        webhookEndpointControl?.setValidators([Validators.required]);
        webhookEndpointControl?.enable();
        webhookEndpointControl?.markAsTouched();
        webhookSecurityKeyControl?.setValidators([Validators.required]);
        webhookSecurityKeyControl?.enable();
        webhookSecurityKeyControl?.markAsTouched();
        this.eventTypeControl.enable();
        this.eventTypesTouched = true;
      }

      writeKeyControl?.updateValueAndValidity();
      webhookEndpointControl?.updateValueAndValidity();
      webhookSecurityKeyControl?.updateValueAndValidity();
    });

    // Initialize the disabled state based on initial provider value
    if (this.configForm.get('provider')?.value === OutboundEventProvider.None) {
      this.configForm.get('writeKey')?.disable();
      this.configForm.get('webhookEndpoint')?.disable();
      this.configForm.get('webhookSecurityKey')?.disable();
      this.eventTypeControl.disable();
    }
  }

  private loadAvailableEventTypes() {
    this.apiClient.getEventTypes().subscribe({
      next: (eventTypes) => {
        this.availableEventTypes = eventTypes as OutboundEvents[];
      },
      error: (error) => {
        console.error('Error loading event types:', error);
        this.snackBar.open('Failed to load available event types', 'Close', { duration: 3000 });
      }
    });
  }

  private setupEventTypeFilter() {
    this.filteredEventTypes = this.eventTypeControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterEventTypes(value || ''))
    );
  }

  private _filterEventTypes(value: string): OutboundEvents[] {
    const filterValue = value.toLowerCase();
    return this.availableEventTypes.filter(eventType =>
      eventType.toLowerCase().includes(filterValue) &&
      !this.selectedEventTypes.includes(eventType)
    );
  }

  private loadConfiguration() {
    if (!this.campaignId) return;

    this.isLoading = true;
    this.apiClient.getCampaignConfig(this.campaignId).subscribe({
      next: (configs) => {
        this.allConfigs = configs || [];
        
        this.currentConfig = this.allConfigs.find(c => c.provider === OutboundEventProvider.Segment)
          || this.allConfigs.find(c => c.provider === OutboundEventProvider.Webhook)
          || this.allConfigs[0] || null;
        
        if (this.currentConfig) {
          this.populateForm(this.currentConfig);
        } else {
          // Initialize with default empty config
          this.populateForm(new OutboundEventIntegrationConfigDto({
            provider: OutboundEventProvider.None,
            credentials: {},
            subscribedEventTypes: []
          }));
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading configuration:', error);
        this.isLoading = false;
        this.allConfigs = [];
        this.currentConfig = null;
        // Initialize with default empty config
        this.populateForm(new OutboundEventIntegrationConfigDto({
          provider: OutboundEventProvider.None,
          credentials: {},
          subscribedEventTypes: []
        }));
      }
    });
  }

  private populateForm(config: OutboundEventIntegrationConfigDto) {
    this.configForm.patchValue({
      provider: config.provider || OutboundEventProvider.None,
      writeKey: config.credentials?.['WriteKey'] || '',
      webhookEndpoint: config.credentials?.['WebhookEndpoint'] || '',
      webhookSecurityKey: config.credentials?.['SecurityKey'] || ''
    });

    this.selectedEventTypes = config.subscribedEventTypes || [];
  }

  addEventType(event: MatChipInputEvent): void {
    const value = (event.value || '').trim() as OutboundEvents;

    if (value && this.availableEventTypes.includes(value) && !this.selectedEventTypes.includes(value)) {
      this.selectedEventTypes.push(value);
    }

    event.chipInput!.clear();
  }

  removeEventType(eventType: OutboundEvents): void {
    const index = this.selectedEventTypes.indexOf(eventType);
    if (index >= 0) {
      this.selectedEventTypes.splice(index, 1);
    }
  }

  onEventTypeSelected(eventType: OutboundEvents): void {
    if (eventType && !this.selectedEventTypes.includes(eventType)) {
      this.selectedEventTypes.push(eventType);
    }
    this.eventTypeControl.setValue('');
    if (this.eventTypeInput) {
      this.eventTypeInput.nativeElement.value = '';
    }
  }

  addEventTypeFromList(eventType: OutboundEvents): void {
    if (!this.selectedEventTypes.includes(eventType)) {
      this.selectedEventTypes.push(eventType);
      this.configForm.markAsDirty();
    }
  }

  onSave() {
    if (this.configForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
      return;
    }

    const formValue = this.configForm.value;
    let credentials: { [key: string]: string } = {};
    if (formValue.provider === OutboundEventProvider.Segment) {
      credentials = { 'WriteKey': formValue.writeKey };
    } else if (formValue.provider === OutboundEventProvider.Webhook) {
      credentials = {
        'WebhookEndpoint': formValue.webhookEndpoint,
        'SecurityKey': formValue.webhookSecurityKey
      };
    }

    const request = new UpdateOutboundEventConfigRequestDto({
      provider: formValue.provider,
      credentials: credentials,
      subscribedEventTypes: this.selectedEventTypes
    });

    this.isSaving = true;
    this.apiClient.updateCampaignConfig(this.campaignId, request).subscribe({
      next: (config) => {
        this.isSaving = false;
        this.snackBar.open('Campaign outbound event configuration saved successfully', 'Close', { duration: 3000 });
        
        this.currentConfig = config;
        const existingIndex = this.allConfigs.findIndex(c => c.provider === config.provider);
        if (existingIndex >= 0) {
          this.allConfigs[existingIndex] = config;
        } else {
          this.allConfigs.push(config);
        }
        
        this.populateForm(config);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error saving configuration:', error);
        this.snackBar.open('Failed to save configuration: ' + (error.response || error.message), 'Close', { duration: 5000 });
      }
    });
  }

  checkConnection() {
    const endpoint = this.configForm.get('webhookEndpoint')?.value;
    const securityKey = this.configForm.get('webhookSecurityKey')?.value;

    if (!endpoint || !securityKey) {
      this.snackBar.open('Webhook endpoint and security key are required', 'Close', { duration: 3000 });
      return;
    }

    const payload = {
      provider: 'Webhook',
      eventType: 'CardReceived',
      data: {
        timestamp: new Date().toISOString()
      }
    };

    const headers = new HttpHeaders({ 'Security-Key': securityKey });

    this.isCheckingConnection = true;
    this.http.post(endpoint, payload, { headers }).subscribe({
      next: () => {
        this.isCheckingConnection = false;
        this.snackBar.open('Connection successful!', 'Close', { duration: 3000 });
      },
      error: (error) => {
        this.isCheckingConnection = false;
        this.snackBar.open('Connection failed: ' + (error.message || error.statusText), 'Close', { duration: 5000 });
      }
    });
  }

  onCancel() {
    this.loadConfiguration();
  }

  get isFormDirty(): boolean {
    return this.configForm.dirty;
  }

  get isProviderNone(): boolean {
    return this.configForm.get('provider')?.value === OutboundEventProvider.None;
  }

  get isProviderSegment(): boolean {
    return this.configForm.get('provider')?.value === OutboundEventProvider.Segment;
  }

  get isProviderWebhook(): boolean {
    return this.configForm.get('provider')?.value === OutboundEventProvider.Webhook;
  }

  // Public methods for parent component integration
  public getFormValue(): UpdateOutboundEventConfigRequestDto | undefined {
    if (!this.configForm.valid) {
      return undefined;
    }

    const formValue = this.configForm.value;
    let credentials: { [key: string]: string } = {};
    if (formValue.provider === OutboundEventProvider.Segment) {
      credentials = { 'WriteKey': formValue.writeKey };
    } else if (formValue.provider === OutboundEventProvider.Webhook) {
      credentials = {
        'WebhookEndpoint': formValue.webhookEndpoint,
        'SecurityKey': formValue.webhookSecurityKey
      };
    }

    return new UpdateOutboundEventConfigRequestDto({
      provider: formValue.provider,
      credentials: credentials,
      subscribedEventTypes: this.selectedEventTypes
    });
  }

  public isValid(): boolean {
    if (this.isProviderNone) return true;
    return this.configForm.valid && this.selectedEventTypes.length > 0;
  }

  public markAsTouched(): void {
    this.configForm.markAllAsTouched();
    if (!this.isProviderNone) {
      this.eventTypesTouched = true;
    }
  }

  public setFormValue(config: OutboundEventIntegrationConfigDto) {
    if (config) {
      this.populateForm(config);
    }
  }
}
